import { filterNewsEntries, NEWS_ENTRIES } from "./news-entries";
import type { NewsEntry, NewsSortFilter } from "./news-types";
import { syncHomeNotificationsBell } from "./notifications-ui";

const PAGE_SIZE = 10;
const KEY_NEWS_SORT_FILTER = "newsSortFilterV1";

const VALID_NEWS_FILTERS: NewsSortFilter[] = [
    "newest",
    "oldest",
    "bug-fix",
    "gameplay",
    "settings",
    "visual",
];

function formatNewsDate(isoDate: string): string {
    const parsed = new Date(`${isoDate}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function readSavedNewsFilter(): NewsSortFilter {
    try {
        const raw = localStorage.getItem(KEY_NEWS_SORT_FILTER);
        if (raw && VALID_NEWS_FILTERS.includes(raw as NewsSortFilter)) {
            return raw as NewsSortFilter;
        }
    } catch {
        /* ignore */
    }
    return "newest";
}

function saveNewsFilter(filter: NewsSortFilter): void {
    try {
        localStorage.setItem(KEY_NEWS_SORT_FILTER, filter);
    } catch {
        /* ignore */
    }
}

function renderNewsItem(entry: NewsEntry): HTMLLIElement {
    const item = document.createElement("li");
    item.className = "homeNewsItem";
    item.dataset.newsId = entry.id;
    item.dataset.newsCategory = entry.category;

    const title = document.createElement("h3");
    title.className = "homeNewsItemTitle";
    title.textContent = entry.title;

    const description = document.createElement("p");
    description.className = "homeNewsItemDesc";
    description.textContent = entry.description;

    const date = document.createElement("time");
    date.className = "homeNewsItemDate";
    date.dateTime = entry.date;
    date.textContent = formatNewsDate(entry.date);

    item.append(title, description, date);
    return item;
}

type NewsListController = {
    reset: () => void;
    setFilter: (filter: NewsSortFilter) => void;
};

function mountNewsList(
    list: HTMLElement,
    loadMoreBtn: HTMLButtonElement,
    emptyEl: HTMLElement | null,
    filterSelect: HTMLSelectElement,
    onFilterChange: (filter: NewsSortFilter) => void,
): NewsListController {
    let visibleCount = PAGE_SIZE;
    let filter: NewsSortFilter = (filterSelect.value as NewsSortFilter) || readSavedNewsFilter();

    function filteredEntries(): NewsEntry[] {
        return filterNewsEntries(filter);
    }

    function syncLoadMore(total: number): void {
        const hasMore = visibleCount < total;
        loadMoreBtn.classList.toggle("hidden", !hasMore || total === 0);
        loadMoreBtn.disabled = !hasMore;
    }

    function render(): void {
        const entries = filteredEntries();
        list.replaceChildren();
        if (emptyEl) {
            emptyEl.classList.toggle("hidden", entries.length > 0);
        }
        for (const entry of entries.slice(0, visibleCount)) {
            list.append(renderNewsItem(entry));
        }
        syncLoadMore(entries.length);
    }

    filterSelect.addEventListener("change", () => {
        filter = filterSelect.value as NewsSortFilter;
        visibleCount = PAGE_SIZE;
        onFilterChange(filter);
        render();
    });

    loadMoreBtn.addEventListener("click", () => {
        visibleCount = Math.min(visibleCount + PAGE_SIZE, filteredEntries().length);
        render();
    });

    render();

    return {
        reset: () => {
            visibleCount = PAGE_SIZE;
            render();
        },
        setFilter: (next: NewsSortFilter) => {
            if (filter === next && filterSelect.value === next) return;
            filter = next;
            filterSelect.value = next;
            visibleCount = PAGE_SIZE;
            render();
        },
    };
}

let newsInHomeEnabled = false;
let homePanelOpen = false;
let screenListController: NewsListController | null = null;
let homeListController: NewsListController | null = null;

export function syncNewsInHomeEnabled(on: boolean): void {
    newsInHomeEnabled = !!on;
    syncHomePanelVisibility();
}

export function resetNewsScreenList(): void {
    screenListController?.reset();
}

function syncHomePanelVisibility(): void {
    const panel = document.getElementById("homeNewsPanel");
    const startScreen = document.getElementById("startScreen");
    if (!panel || !startScreen) return;

    const onHome =
        newsInHomeEnabled &&
        !startScreen.classList.contains("hidden") &&
        !document.body.classList.contains("account-bootstrap-active");

    panel.classList.toggle("hidden", !onHome);
    syncHomeNotificationsBell();
}

export function mountNewsUi(): void {
    const panel = document.getElementById("homeNewsPanel");
    const tab = document.getElementById("homeNewsPanelTab");
    const homeList = document.getElementById("homeNewsList");
    const homeLoadMore = document.getElementById("homeNewsLoadMore");
    const homeFilter = document.getElementById("homeNewsFilter");
    const homeEmpty = document.getElementById("homeNewsEmpty");
    const screenList = document.getElementById("newsScreenList");
    const screenLoadMore = document.getElementById("newsScreenLoadMore");
    const screenFilter = document.getElementById("newsScreenFilter");
    const screenEmpty = document.getElementById("newsScreenEmpty");
    const startScreen = document.getElementById("startScreen");

    if (
        !panel || !tab || !homeList || !homeLoadMore || !homeFilter ||
        !screenList || !screenLoadMore || !screenFilter || !startScreen ||
        !(homeLoadMore instanceof HTMLButtonElement) ||
        !(screenLoadMore instanceof HTMLButtonElement) ||
        !(homeFilter instanceof HTMLSelectElement) ||
        !(screenFilter instanceof HTMLSelectElement)
    ) {
        return;
    }

    if (!NEWS_ENTRIES.length) {
        return;
    }

    const savedFilter = readSavedNewsFilter();
    homeFilter.value = savedFilter;
    screenFilter.value = savedFilter;

    function handleNewsFilterChange(filter: NewsSortFilter): void {
        saveNewsFilter(filter);
        homeFilter.value = filter;
        screenFilter.value = filter;
        homeListController?.setFilter(filter);
        screenListController?.setFilter(filter);
    }

    homeListController = mountNewsList(homeList, homeLoadMore, homeEmpty, homeFilter, handleNewsFilterChange);
    screenListController = mountNewsList(screenList, screenLoadMore, screenEmpty, screenFilter, handleNewsFilterChange);

    function syncTabArrow(): void {
        tab.setAttribute("aria-expanded", homePanelOpen ? "true" : "false");
        tab.setAttribute("aria-label", homePanelOpen ? "Close news panel" : "Open news panel");
        panel.classList.toggle("homeNewsPanel--open", homePanelOpen);
    }

    tab.addEventListener("click", () => {
        homePanelOpen = !homePanelOpen;
        syncTabArrow();
    });

    const observer = new MutationObserver(syncHomePanelVisibility);
    observer.observe(startScreen, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    syncTabArrow();
    syncHomePanelVisibility();
}
